/**
 * Component for rendering program workouts (from MicroCycleDay)
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Container, Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { MicroCycleDay } from '../../../Model/Coach/Program/MicroCycleDay';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { Theme } from '../../../Theme/Theme';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { ImmersiveWorkout } from '../../../Components/EntryView/ExploreContentViewWorkoutTemplate/ImmersiveWorkout';
import { EndWorkoutOverviewModal } from '../../../Components/EntryView/ExploreContentViewWorkoutTemplate/EndWorkoutOverviewModal';
import { reaction, runInAction } from 'mobx';
import { EMPTY_ARRAY } from '../../../Utils/Constants';
import { ViewPhase } from '../../../Model/WorkoutLog/ViewPhase';
import { ViewBlock } from '../../../Model/WorkoutLog/ViewBlock';
import { WorkoutTemplatePhase } from '../../../Components/EntryView/ExploreContentViewWorkoutTemplate/WorkoutTemplatePhase';
import playIcon from '../../../Assets/Images/play_icon.png';

const LoadingContainer = styled.div`
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingSpinner = styled(Spinner)`
  width: 3rem;
  height: 3rem;
  color: ${Theme.Colors.primary};
`;

const ImageContainer = styled.div<{ backgroundImageUrl?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 16px;
  background: ${({ backgroundImageUrl }) => (backgroundImageUrl ? `url('${backgroundImageUrl}')` : 'none')};
  background-size: cover !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  height: 300px;
  border-radius: 16px 16px 0px 0px;
  margin-bottom: 16px;
  cursor: pointer;

  @media (min-width: 600px) {
    height: 360px;
    margin-bottom: 32px;
  }

  @media (min-width: 992px) {
    height: 480px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
    border-radius: 16px 16px 0 0;
  }
`;

const OverlayPlayButton = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 64px;
  height: 64px;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 10;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px;
`;

type SectionData = { phase: ViewPhase; data: ViewBlock[] };

export type ProgramWorkoutViewProps = {
  microCycleDay: MicroCycleDay;
};

export const ProgramWorkoutView: React.FC<ProgramWorkoutViewProps> = observer(({ microCycleDay }) => {
  const { workoutViewStore } = useRootStore();
  const { t } = useTrans();
  const workoutName = useLocalized(microCycleDay.workout?.name);
  const programTemplateName = useLocalized(microCycleDay.program?.template?.name);

  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<SectionData[]>([]);
  const [showCountdown, setShowCountdown] = useState(false);

  const workout = microCycleDay.workout;
  const title = workoutName ?? '';
  const image = workout?.largeWorkoutImage?.uri;
  const programName = programTemplateName;

  // Initialize workout from microCycleDay
  useEffect(() => {
    if (workout) {
      setLoading(true);
      workoutViewStore.selectMicroCycleDay(microCycleDay).finally(() => setLoading(false));
    }
  }, [workout, microCycleDay, workoutViewStore]);

  // Update sections when view phases change
  useEffect(
    () =>
      reaction(
        () => workoutViewStore.viewWorkout?.viewPhases ?? EMPTY_ARRAY,
        (viewPhases) =>
          setSections(
            viewPhases.reduce((arr, phase) => {
              const prev = arr[arr.length - 1];
              if (prev?.phase.type === phase.type) {
                prev.data = prev.data.concat(phase.exerciseBlocks);
              } else {
                arr.push({ phase: phase, data: phase.exerciseBlocks.slice() });
              }
              return arr;
            }, [] as SectionData[]),
          ),
      ),
    [workoutViewStore],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => workoutViewStore.workoutRunner.resetWorkout();
  }, [workoutViewStore]);

  if (workoutViewStore.isWorkoutActive || showCountdown) {
    return <ImmersiveWorkout onExit={() => setShowCountdown(false)} />;
  }

  if (loading || !workout) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <ImageContainer backgroundImageUrl={image} onClick={() => setShowCountdown(true)}>
        <OverlayPlayButton src={playIcon} alt="Play" />
      </ImageContainer>
      <Title>{title}</Title>
      {programName && <Subtitle>{programName}</Subtitle>}
      {sections.map(({ phase, data }) => (
        <WorkoutTemplatePhase key={phase.viewPhaseId} phase={phase} data={data} />
      ))}
      {workoutViewStore.endWorkoutOverviewModalVisible && <EndWorkoutOverviewModal />}
    </Container>
  );
});
